import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { BOOKINGS_TABS, BOOKINGS_VIEWS } from '../Bookings';
import { bookingUpdated } from '../Redux/BookingGlobalActions';
import { getBookingListData, getPagedData } from '../Requests/BookingListRequests';
import { getCategorySummaries } from "../Requests/BookingListRequests";
import { getColumns } from './Table/BookingsListColumns';
import BookingActionsCell from "./Table/Cells/BookingActionsCell";
import ExportModal from "./Modals/ExportModal";
import SearchedAtSignInModal from "./Modals/SearchedAtSignInModal";
import TimelineTable, { LOADING } from "../../../Components/Table/TimelineTable";
import BookingListFilterRow from "./Table/Cells/BookingListFilterRow";
import { MODAL_EDIT_BOOKING, TabContext } from "../Pages/BookingsLog";
import { hideModal } from "../../../Redux/GlobalReducer";
import { countConcurrentVisitsAtEnd, countConcurrentVisitsAtStart } from "../../Visitors/Helpers/VisitsHelper";
import { resetBookingFlow } from "../Redux/BookingFormSlice";

const BookingsList = (props) => {
    const dispatch = useDispatch();
    const tabState = useContext(TabContext);

    const moduleConfig = useSelector(state => state.module.BOOKINGS.common.config);

    const filters = useSelector(state => state.module.BOOKINGS.filters);

    const categorySummaries = useSelector(state => state.module.BOOKINGS.local.categorySummaries);
    const hasBookingUpdated = useSelector(state => state.module.BOOKINGS.bookingForm.bookingUpdated);

    const recordsAreLoading = useSelector(state => state.module.BOOKINGS.bookingList[tabState.view][tabState.scope].recordsAreLoading);
    const hasMoreRecordsAtEnd = useSelector(state => state.module.BOOKINGS.bookingList[tabState.view][tabState.scope].hasMoreRecordsAtEnd);
    const hasMoreRecordsAtStart = useSelector(state => state.module.BOOKINGS.bookingList[tabState.view][tabState.scope].hasMoreRecordsAtStart);

    const [ selectedCategory, setSelectedCategory ] = React.useState(null);
    const [ showExportModal, setShowExportModal ] = React.useState(false);

    useEffect(() => {
        // Category summaries might not have been set yet if you didn't navigate here from the booking page
        if (!categorySummaries) {
            dispatch( getCategorySummaries() );
        }
    }, []);

    useEffect(() => {
        let category = null;
        if (categorySummaries && filters.category) {
            category = Object.values(categorySummaries).find((cat) => cat.slug === filters.category);
        }
        setSelectedCategory(category);
    }, [filters.category, categorySummaries])

    // Refresh booking list data when a booking has been changed
    useEffect(() => {
        if (hasBookingUpdated) {
            dispatch( getBookingListData(BOOKINGS_TABS.UPCOMING, tabState.view, true) );
            dispatch( hideModal(MODAL_EDIT_BOOKING) )
            dispatch( resetBookingFlow() );
            dispatch( bookingUpdated(false) );
        }
    }, [ hasBookingUpdated ]);


    const handleLoadAtStart = () => {
        let firstKey = Object.keys(props.rows)[0];
        let pivotDate = props.rows[firstKey].start;

        const concurrentCount = countConcurrentVisitsAtStart(props.rows, (obj) => obj.start);

        let earlierThan, laterThan, direction;

        if (tabState.scope === BOOKINGS_TABS.UPCOMING) {
            earlierThan = pivotDate;
            laterThan = null;
            direction = 'desc';
        } else {
            earlierThan = null;
            laterThan = pivotDate;
            direction = 'asc'
        }

        dispatch(
            getPagedData(tabState, laterThan, earlierThan, LOADING.AT_START, direction, concurrentCount)
        );
    }

    const handleLoadAtEnd = () => {
        const lastKey = Object.keys(props.rows).pop();
        const pivotDate = props.rows[lastKey].start;

        const concurrentCount = countConcurrentVisitsAtEnd(props.rows, (obj) => obj.start);

        let earlierThan, laterThan, direction;

        if (tabState.scope === BOOKINGS_TABS.UPCOMING) {
            earlierThan = null;
            laterThan = pivotDate;
            direction = 'asc';
        } else {
            earlierThan = pivotDate;
            laterThan = null;
            direction = 'desc'
        }

        dispatch(
            getPagedData(tabState, laterThan, earlierThan, LOADING.AT_END, direction, concurrentCount)
        );
    }

    return (<>

        <TimelineTable
            FilterRowComponent={BookingListFilterRow}
            ActionsCellComponent={BookingActionsCell}

            data={props.rows}
            columnData={getColumns(tabState.scope, tabState.view, selectedCategory, moduleConfig.enableSignIn)}
            filters={filters}

            loadingState={recordsAreLoading}
            hasMoreRecordsAtEnd={hasMoreRecordsAtEnd}
            hasMoreRecordsAtStart={hasMoreRecordsAtStart}
            onLoadAtStart={handleLoadAtStart}
            onLoadAtEnd={handleLoadAtEnd}

            onExport={(tabState.scope === BOOKINGS_TABS.UPCOMING) ? () => setShowExportModal(true) : null}
            isTimeResponsive={tabState.scope === BOOKINGS_TABS.UPCOMING}
        />

        <ExportModal
            open={showExportModal}
            onClose={() => setShowExportModal(false)}
            scope={tabState.scope}
            view={tabState.view}
        />

        <SearchedAtSignInModal/>
    </>)
}

BookingsList.defaultProps = {
    scope: BOOKINGS_TABS.UPCOMING,
    isTimeResponsive: false,
    groupByDate: false,
    actions: [],
    view: BOOKINGS_VIEWS.ALL_BOOKINGS
}

export default BookingsList;
